/** The activity category record's one card (plan §4 "One card", §2 "Cards"; O8): a
 *  `RecordPanel` over the category registry — the Editable block (Title, Icon, Access tags)
 *  then the System block (Activities) — as view-mode FieldSlots, the stored values from the
 *  detail.
 *
 *  - Icon: the icon as the app shows it, downsized in the records image row's 48 px tile
 *    (`ImageView`, shape `tile`; a vector as stored) that opens the original in a new tab,
 *    and its file name; "No icon" in words when it has none — the tile and the words the
 *    edit-mode input draws, so pressing E moves nothing.
 *  - Activities: how many, opening the activities grid filtered by the category.
 *
 *  The records' width rule: the field grid is pinned to the column count the card has beside
 *  the docked Changes column from 1100 px of content (`spineColumns`: content − 340 − 16) —
 *  in both modes, as the column is (the page), so entering edit mode re-lays nothing out.
 *  The access tags take the row, as the activity's do. */
import { Box } from '@mui/material';
import { useMemo, type ReactNode } from 'react';

import type { ActivityCategoryDetail } from '@/api/activityCategories';
import {
  ACTIVITY_CATEGORY_RECORD_SECTIONS,
  type ActivityCategoryRecordFieldKey,
  type ActivityCategoryRecordSection,
} from '@/api/activityCategoryRecord';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { categoryActivitiesActions, type CatalogCtx } from '@/features/catalog/catalogLinks';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { ImageView } from '@/features/records/fields/widgets/ImageWidget';
import { FIELD_GRID_SX, FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { RecordPanel } from '@/features/records/panels/RecordPanel';
import { spineColumns } from '@/features/records/views/spineColumns';
import type { FieldBlockKind, ViewBlock, ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { categoryIconImage } from './editorConfig';
import { activityCountLabel } from './parts/activitiesInCategoryModel';
import {
  ACTIVITY_CATEGORY_ENTITY,
  ACTIVITY_CATEGORY_RECORD_REGISTRY,
  ACTIVITY_CATEGORY_SECTION_TITLES,
  activityCategoryPlacements,
  type ActivityCategoryRegistry,
} from './registry';
import { NO_ICON } from './widgets';

/** The DOM id of a card — what the number keys scroll to. */
export function categorySectionAnchorId(section: ActivityCategoryRecordSection): string {
  return `category-section-${section}`;
}

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'system'];

/** The registry and one category → its card, split into the Editable and System blocks.
 *  Pure — the FieldSlots read the draft themselves. */
export function categorySections(
  c: Pick<ActivityCategoryDetail, 'id'>,
  registry: ActivityCategoryRegistry = ACTIVITY_CATEGORY_RECORD_REGISTRY,
): ViewSection<ActivityCategoryRecordSection>[] {
  const placements = activityCategoryPlacements(registry);
  return ACTIVITY_CATEGORY_RECORD_SECTIONS.map((section) => {
    const keys = placements.get(section) ?? [];
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields = keys
        .filter((k) => registry[k].block === kind)
        .map((k) => ({ key: k, entity: ACTIVITY_CATEGORY_ENTITY, id: c.id, path: k }));
      if (fields.length === 0) continue;
      blocks.push({ kind, ...(kind === 'system' ? { label: 'System' } : {}), fields });
    }
    return { id: section, title: ACTIVITY_CATEGORY_SECTION_TITLES[section], blocks };
  });
}

export interface CategoryDisplayContext {
  detail: ActivityCategoryDetail;
  links: CatalogCtx;
}

/** A field's rendering in place of its stored value as text — the icon's and the activity
 *  count's; undefined = the value as text. */
export function categoryFieldDisplay(key: ActivityCategoryRecordFieldKey, c: CategoryDisplayContext): ReactNode | undefined {
  const { detail } = c;
  switch (key) {
    case 'icon':
      return <ImageView image={categoryIconImage(detail)} label="Icon" shape="tile" emptyText={NO_ICON} />;
    case 'activity_count':
      return detail.activity_count > 0 ? (
        <ValueActions actions={categoryActivitiesActions(detail.id, c.links)}>
          {activityCountLabel(detail.activity_count)}
        </ValueActions>
      ) : (
        'No activities'
      );
    default:
      return undefined;
  }
}

/** A linkable editable value's actions, from its stored value; undefined = none. */
export function categoryFieldActions(key: ActivityCategoryRecordFieldKey): ((value: unknown) => ValueAction[]) | undefined {
  return key === 'title' ? (v) => (typeof v === 'string' && v.trim() ? [act.copy(v, 'Copy title')] : []) : undefined;
}

/** Values that take the whole row: the access tags (a list, as on the activity). */
const WIDE: ReadonlySet<ActivityCategoryRecordFieldKey> = new Set(['atag']);
/** The icon's row — its tile, its type ("JPG icon") and edit mode's Replace… and Remove —
 *  needs two columns: in one, the buttons left its words three letters. */
const SPAN: Readonly<Partial<Record<ActivityCategoryRecordFieldKey, number>>> = { icon: 2 };

function CategoryFieldGrid({ fields, display }: { fields: ViewFieldRef[]; display: CategoryDisplayContext }) {
  const { fieldByKey } = useRecordEditor();
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={fields.map((f) => {
        const key = f.key as ActivityCategoryRecordFieldKey;
        const shown = categoryFieldDisplay(key, display);
        const actions = categoryFieldActions(key);
        return {
          key: `${f.entity}:${f.id}:${f.key}`,
          rec: `${f.entity}:${f.id}`,
          path: f.path,
          wide: WIDE.has(key) && fieldByKey.has(key),
          ...(SPAN[key] ? { span: SPAN[key] } : {}),
          node: (
            <FieldSlot
              fieldKey={f.key}
              entity={f.entity}
              id={f.id}
              path={f.path}
              inlineChoices={false}
              {...(shown !== undefined ? { display: shown } : {})}
              {...(actions ? { actions } : {})}
            />
          ),
        };
      })}
    />
  );
}

export interface CategoryCardProps {
  detail: ActivityCategoryDetail;
  /** The page content's width (the ledger's column included in edit mode); 0 = unmeasured. */
  width: number;
}

export function CategoryCard({ detail, width }: CategoryCardProps) {
  const { editWidth } = spineColumns(width);
  const sections = useMemo(() => categorySections(detail), [detail]);
  const display = useMemo<CategoryDisplayContext>(
    () => ({ detail, links: { tenantId: detail.tenant.id } }),
    [detail],
  );
  const pin = width > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(editWidth) } : {};
  return (
    <Box data-category-card sx={{ display: 'grid', gap: 2, minWidth: 0, ...pin }}>
      {sections.map((section) => (
        <RecordPanel
          key={section.id}
          section={section}
          anchorId={categorySectionAnchorId(section.id)}
          renderFields={(g) => <CategoryFieldGrid fields={g.fields} display={display} />}
        />
      ))}
    </Box>
  );
}
